import './styles.sass';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { findIndex } from 'lodash-es';

import AwesomeBarSearchManager from 'models/awesome_bar_search_manager';

import SearchLoadingIcon from '../SearchLoadingIcon';
import ResultView from './result_view';
import SearchTerm from 'models/SearchTerm';

import { attachFocusLimiter, detachFocusLimiter } from '../util/focus_limiter';
import { closeAllPopovers } from 'util/popover';
import useQueryParams from 'hooks/useQueryParams';

// SelectionIndexPath is modelled on Cocoa's NSIndexPath — an array of indexes
// that walks down a tree structure. `[0, 0]` is the first item in the first
// set; `[2, 3]` is the fourth item in the third set. This lets us increment
// or decrement a single position to navigate the (resultSet, result) tree.

const QuickSearch = ({ onSelectResult, onSelectShowMore }) => {
  const params = useQueryParams();

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [resultSets, setResultSets] = useState([]);
  const [selectionIndexPath, setSelectionIndexPath] = useState([0, 0]);
  const [scope, setScope] = useState('all');

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const searchManagerRef = useRef(null);
  const textEnteredRef = useRef(false);

  const currentScopeFromURL = () => (params.filters?.scope === 'all' ? 'all' : 'institution');

  const tabIndex = expanded ? 0 : -1;

  const killSearch = () => {
    if (searchManagerRef.current) searchManagerRef.current.terminate();
    setResultSets([]);
    setSelectionIndexPath([0, 0]);
  };

  const startSearch = (newText) => {
    searchManagerRef.current = new AwesomeBarSearchManager(newText, {
      updateCallback: didUpdateSearch,
      completeCallback: () => setLoading(false)
    });
    setLoading(true);
    searchManagerRef.current.startLoading();
  };

  const didUpdateSearch = (newResultSets) => {
    setResultSets(newResultSets);

    // If we have an identifier match, immediately select that. This is because
    // in almost every case, if the user enters an actual identifier they will
    // want to search for it immediately rather than doing a keyword search.
    const identifierIndex = findIndex(newResultSets, (r) => r.type == 'identifier');
    if (identifierIndex >= 0) setSelectionIndexPath([identifierIndex, 0]);

    // As the above, immediately select an ORCiD result if it is available
    const orcidIndex = findIndex(newResultSets, (r) => r.type == 'orcid');
    if (orcidIndex >= 0) setSelectionIndexPath([orcidIndex, 0]);
  };

  const show = () => {
    closeAllPopovers();
    setExpanded(true);
    setScope(currentScopeFromURL());
    attachFocusLimiter(containerRef.current);
  };

  const hide = ({ trackClosed = false } = {}) => {
    killSearch();
    inputRef.current?.focus();
    setExpanded(false);
    setText('');
    setLoading(false);
    detachFocusLimiter(containerRef.current);
    if (trackClosed) Analytics.trackEvent('Quick Search: Dialogue Closed');
  };

  const trackTextAnalytics = (newText) => {
    if (!textEnteredRef.current && newText.trim().length > text.length) {
      textEnteredRef.current = true;
      Analytics.trackEvent('Quick Search: Field Filled');
    }
    if (textEnteredRef.current && newText.trim() === '') {
      Analytics.trackEvent('Quick Search: Field Manually Cleared');
      textEnteredRef.current = false;
    }
  };

  const updateText = (newText) => {
    if (!expanded) setExpanded(true);
    killSearch();
    trackTextAnalytics(newText);
    setText(newText);

    if (newText.trim() === '') {
      setLoading(false);
    } else {
      startSearch(newText);
    }
  };

  const selectedResult = () => {
    const branch = resultSets[selectionIndexPath[0]];
    if (!branch) return undefined;
    return branch.results[selectionIndexPath[1]];
  };

  const selectResult = (result) => {
    hide();
    Analytics.trackEvent('Quick Search: Search Applied', { value: text });

    const filters = result ? result.filters : {};
    if (window.current_user.institution_integrated) {
      filters.scope = scope;
    }

    if (result && result.isLinkToMore) {
      if (onSelectShowMore) {
        const searchTerm = new SearchTerm(result.isLinkToMore, text);
        inputRef.current?.blur();
        onSelectShowMore(searchTerm);
      }
    } else if (onSelectResult) {
      onSelectResult(filters);
    }
  };

  const moveSelectionUp = () => {
    const ip = selectionIndexPath;
    const rs = resultSets;
    if (rs.length === 0 || !rs[ip[0]]) return;

    const hasPreviousResultsInSet = ip[1] > 0;
    const hasPreviousSetsInList = ip[0] > 0;

    if (hasPreviousResultsInSet) {
      setSelectionIndexPath([ip[0], ip[1] - 1]);
    } else if (hasPreviousSetsInList) {
      const previousResultSet = rs[ip[0] - 1];
      setSelectionIndexPath([ip[0] - 1, previousResultSet.results.length - 1]);
    } else {
      const lastResultSet = rs[rs.length - 1];
      setSelectionIndexPath([rs.length - 1, lastResultSet.results.length - 1]);
    }
  };

  const moveSelectionDown = () => {
    const ip = selectionIndexPath;
    const rs = resultSets;
    if (rs.length === 0 || !rs[ip[0]]) return;

    const hasMoreResultsInSet = rs[ip[0]].results.length > ip[1] + 1;
    const hasMoreSetsInList = rs.length > ip[0] + 1;

    if (hasMoreResultsInSet) {
      setSelectionIndexPath([ip[0], ip[1] + 1]);
    } else if (hasMoreSetsInList) {
      setSelectionIndexPath([ip[0] + 1, 0]);
    } else {
      setSelectionIndexPath([0, 0]);
    }
  };

  const onKeyUp = (e) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        hide();
        Analytics.trackEvent('Quick Search:  Dialogue Cancelled');
        break;
      case 'Enter':
        e.preventDefault();
        selectResult(selectedResult());
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveSelectionUp();
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveSelectionDown();
        break;
    }
  };

  const onKeyUpButton = (e) => {
    if (e.key === 'Escape') {
      hide();
      Analytics.trackEvent('Quick Search:  Dialogue Cancelled');
    }
  };

  const handleFocus = () => {
    if (!expanded) {
      show();
      Analytics.trackEvent('Quick Search: Field Selected');
    }
  };

  const handleClose = () => {
    hide({ trackClosed: true });
  };

  const setScopeAll = (e) => {
    e.preventDefault();
    setScope('all');
    Analytics.trackEvent('Quick Search: Scope Changed', { scope: 'full' });
  };

  const setScopeInstitution = (e) => {
    e.preventDefault();
    setScope('institution');
    Analytics.trackEvent('Quick Search: Scope Changed', { scope: 'institution' });
  };

  useEffect(() => {
    return () => {
      if (searchManagerRef.current) searchManagerRef.current.terminate();
    };
  }, []);

  return (
    <search
      ref={containerRef}
      className={`Explorer-QuickSearch ${expanded ? 'expanded' : 'not-expanded'}`}
      role="search"
      aria-label={I18n.t('Explorer.QuickSearch.aria_label')}
    >
      <button
        className="Explorer-QuickSearch-close-button"
        onClick={handleClose}
        tabIndex={tabIndex}
        onKeyUp={onKeyUpButton}
      >
        {I18n.t('Explorer.QuickSearch.close')}
      </button>

      <div className="Explorer-QuickSearch-field-wrapper">
        <SearchLoadingIcon loading={loading} />
        <input
          ref={inputRef}
          className="Explorer-QuickSearch-field"
          placeholder={I18n.t('Explorer.QuickSearch.placeholder')}
          value={text}
          onFocus={handleFocus}
          onChange={(e) => updateText(e.target.value)}
          onKeyUp={onKeyUp}
          aria-label={I18n.t('Explorer.QuickSearch.placeholder')}
        />
        {window.current_user.institution_integrated && (
          <div className="Explorer-Quicksearch-institutional-toggle">
            <button
              onClick={setScopeAll}
              className={`full ${scope == 'all' ? 'active' : 'not-active'}`}
              tabIndex={tabIndex}
              onKeyUp={onKeyUpButton}
            >
              {I18n.t('Explorer.QuickSearch.institution_toggle.full')}
            </button>

            <button
              onClick={setScopeInstitution}
              className={`institution ${scope == 'institution' ? 'active' : 'not-active'}`}
              tabIndex={tabIndex}
              onKeyUp={onKeyUpButton}
            >
              {I18n.t('Explorer.QuickSearch.institution_toggle.institution')}
            </button>
          </div>
        )}
      </div>

      {resultSets.length ? (
        <div className="Explorer-QuickSearch-results">
          <ResultView
            resultSets={resultSets}
            selectedResult={selectedResult()}
            onSelectResult={selectResult}
            loading={loading}
          />
        </div>
      ) : null}
    </search>
  );
};

QuickSearch.propTypes = {
  onSelectResult: PropTypes.func,
  onSelectShowMore: PropTypes.func
};

export default QuickSearch;
